import React, { useState } from 'react'
import { motion } from "framer-motion"
import { fadeIn } from "../utils/motion"
import { useLocation, useNavigate } from "react-router-dom"
import { HashLink } from 'react-router-hash-link'
import { useMsal } from "@azure/msal-react"

import vivaceLogo from "/vivace_logo.webp"
import xLogo from "/x.svg"
import menuLogo from "/menu.svg"

const Navbar = ({ links }) => {
	const navigate = useNavigate()
	const { instance } = useMsal()
	const activeAccount = instance.getActiveAccount()
	const [isMenuOpen, setIsMenuOpen] = useState(false)
	const [activeLink, setActiveLink] = useState('#home')
	const location = useLocation() // Get current route

	// Default links if none are passed via props
	const navLinks = links && links.length ? links : [
		{ href: "#home", label: "Home" },
		{ href: "#about", label: "About Us" },
		{ href: "#services", label: "Our Service" },
		{ href: "#newsletter", label: "Contact Us" },
	]

	// Scroll and update active link
	const scrollToSection = (id) => {
		setActiveLink(id)
		setIsMenuOpen(false)
	}

	// Disable animations on dashboard page
	const disableAnimations = location.pathname === "/dashboard"

	return (
		<motion.nav
			{...(!disableAnimations
				? {
					variants: fadeIn('down', 0.2),
					initial: "hidden",
					whileInView: "show",
					viewport: { once: true }
				}
				: {})}
			className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-sm z-50 border-b border-gray-100 shadow-sm"
		>
			<div className="w-full flex justify-between items-center container mx-auto px-4 sm:px-6 lg:px-8 h-25 md:h-20">

				{/* Logo + Desktop Links */}
				<motion.div
					{...(!disableAnimations ? { variants: fadeIn('right', 0.3) } : {})}
					className="flex items-center gap-1 cursor-pointer"
				>
					<HashLink
					to="/#home"
					smooth
					onClick={() => setActiveLink('#home')}
					className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
					aria-current={activeLink === '#home' ? 'page' : undefined}
					title="Go to Home section"
					>
						<img src={vivaceLogo} className="w-18 h-10 rounded-full opacity-90" alt="Vivace International"/>
					</HashLink>

					{/* Desktop Navigation Links */}
					<motion.div
						{...(!disableAnimations ? { variants: fadeIn('down', 0.3) } : {})}
						className="hidden md:flex items-center divide-x divide-black-300"
					>
						{navLinks.map((link, index) => (
							<HashLink
								key={index}
								smooth
								to={link.href}
								onClick={() => scrollToSection(link.href)}
								className={`px-4 lg:px-8 text-sm md:text-base font-medium relative
									${activeLink === link.href ? 'text-blue-600' : 'text-gray-600 hover:text-blue-900'}`}
							>
								{link.label}
							</HashLink>
						))}
					</motion.div>
				</motion.div>

				{/* Mobile Menu Button */}
				<motion.button
					{...(!disableAnimations ? { variants: fadeIn('left', 0.3) } : {})}
					className="md:hidden p-2"
					onClick={() => setIsMenuOpen(!isMenuOpen)}
				>
					{isMenuOpen ? (
						<img src={xLogo} alt="close" className="w-8 h-8" />
					) : (
						<img src={menuLogo} alt="menu" className="w-8 h-8" />
					)}
				</motion.button>

				{/* CTA Button - Desktop */}
				<motion.button
					{...(!disableAnimations ? { whileHover: { scale: 1.05 }, whileTap: { scale: 0.95 } } : {})}
					className="hidden md:block bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 text-sm font-medium transition-all hover:shadow-lg hover:shadow-blue-100"
				>
					{activeAccount ? (
						<a href="/dashboard" className="block max-w-[200px] truncate" title={activeAccount.name}>
							Dashboard for {activeAccount.name}
						</a>
					) : (
						<a href="/login">Log In</a>
					)}
				</motion.button>
			</div>

			{/* Mobile Menu */}
			{isMenuOpen && (
				<motion.div
					{...(!disableAnimations
						? { variants: fadeIn('down', 0.2), initial: "hidden", animate: "show" }
						: {})}
					className="md:hidden bg-white border-t border-gray-100 py-4"
				>
					<div className="container mx-auto px-4 space-y-4">
						{navLinks.map((link, index) => (
							<HashLink
								key={index}
								smooth
								to={link.href}
								onClick={() => scrollToSection(link.href)}
								className={`block text-xl font-medium py-2
									${activeLink === link.href ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
							>
								{link.label} 
							</HashLink>
						))}
						{/* Mobile Login Button */}
						<button
							className="w-full bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 text-xl 
    							font-medium transition-all hover:shadow-lg hover:shadow-blue-100"
							onClick={() => navigate("/login")}
						>
							Login
						</button>
					</div>
				</motion.div>
			)}
		</motion.nav>
	)
}

export default Navbar
